import { useCreateBeneficiaryMutation } from "@/modules/transaction/services";
import { BeneficiaryType, ErrorMessages, NotificationScope, } from "@/shared/common/enums";
import { Notification } from "@/shared/components";
import { trimAll } from "@/shared/helpers";
import { useNotify } from "@/shared/hooks";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  refetch: () => void;
  creditCardNumber: string;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsBenSaved: Dispatch<SetStateAction<boolean>>;
}

export const SaveBeneficiaryModal = ({
  isOpen,
  refetch,
  setIsOpen,
  setIsBenSaved,
  creditCardNumber,
}: Props) => {
  const { t } = useTranslation();
  const { showErrorAlertNotification } = useNotify();

  const [createNewBeneficiary, { isLoading }] = useCreateBeneficiaryMutation();

  const [beneficiaryName, setBeneficiaryName] = useState<string>("");
  const [beneficiaryNameError, setBeneficiaryNameError] = useState<string>("");

  const handleChangeBeneficiaryName = (name: string) => {
    setBeneficiaryName(name);
    setBeneficiaryNameError("");
  };
  const handleSaveBeneficiary = () => {
    if (!trimAll(beneficiaryName)) {
      setBeneficiaryNameError(t(ErrorMessages.RequiredField));
      return;
    }

    createNewBeneficiary({
      number: trimAll(creditCardNumber),
      name: beneficiaryName,
      beneficiaryFund: BeneficiaryType.Card,
    })
      .unwrap()
      .then(() => {
        refetch();
        setIsBenSaved(true);
        setIsOpen(false);
      })
      .catch((err) => {
        showErrorAlertNotification(err, NotificationScope.Modal);
        setIsBenSaved(false);
      });
  };

  useEffect(() => {
    return () => {
      setBeneficiaryName("");
      setBeneficiaryNameError("");
    };
  }, []);

  return (
    <Modal
      onSubmit={handleSaveBeneficiary}
      titleProps={{
        title: t("transfer.saveBeneficiary.title"),
        size: "large",
      }}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      buttonProps={{
        confirm: {
          buttonText: t("general.save"),
          buttonActionType: "button",
          disabled: isLoading,
          isLoading,
        },
        cancel: {
          buttonText: t("general.cancel"),
          disabled: isLoading,
        },
      }}
    >
      <Notification scope={NotificationScope.Modal}/>
      <FormField
        name="beneficiary"
        className="mt-16"
        errorMessageIcon="infoFilled"
        As={(props) => (
          <Input
            {...props}
            required
            handleChange={(_, value) => handleChangeBeneficiaryName(value)}
            error={beneficiaryNameError}
            hasError={!!beneficiaryNameError}
            label={t("transfer.beneficiaryName")}
            placeholder={t("transfer.enterBeneficiaryName")}
          />
        )}/>
    </Modal>
  );
};
